// What stands on a room's floor: each part standing in it, what of it stands
// there, its whole outline on the floor and its height band over the floor.
// THE one answer to "what is on this floor, and where"; its readers each pick
// what carves them: the grille (floorGrille.ts `grilleCutsOf`), the backrests
// (benchRest.ts `backrestCutsOf`) and the benches (roomRegen.ts
// `heaterHoles`). Pure and DOM-free (test/floorOccupancy.test.mjs;
// docs/claude/rooms.md, *What stands on the floor*).

import type {
  ProductInstance,
  Vec2,
} from '../shared/types';
import {
  benchSupportMarks,
  nodeBenchSkirtFront,
} from './benchSolids';
import {
  instanceToWorld,
  partOuterCorners,
  pointInPolygon,
  ribbonOutline,
  toInstanceLocal,
} from './geometry';
import {
  partHeight,
  partSpan,
  roomBase,
} from './heights';
import { inWallBand } from './mounting';
import {
  isTilted,
  tiltedExtents,
} from './tilt';
import { traitsOf } from '../shared/categories';

/** How far in from its own outline a part's footprint is taken (mm): touching a
 *  bench (or a backrest) is not standing over it. */
const INSET = 1;

/** A point of a part's frame in a room's (both plan frames: x, y, rotation). */
function inRoom(room: ProductInstance, part: ProductInstance, p: Vec2): Vec2 {
  return toInstanceLocal(room, instanceToWorld(part, p));
}

/** What of a part stands in the room, in the room's frame: its front band out of
 *  the wall when it is set into one (`inWall`), else its whole footprint — taken
 *  `inset` in (by default {@link INSET}). */
export function standingFoot(room: ProductInstance, part: ProductInstance, inset = INSET): Vec2[] {
  const b = inWallBand(part) ?? { x0: -part.width / 2, y0: -part.depth / 2, x1: part.width / 2, y1: part.depth / 2 };
  const x0 = b.x0 + inset, x1 = b.x1 - inset, y0 = b.y0 + inset, y1 = b.y1 - inset;
  if (x1 <= x0 || y1 <= y0) return [];
  return [{ x: x0, y: y0 }, { x: x1, y: y0 }, { x: x1, y: y1 }, { x: x0, y: y1 }].map((p) => inRoom(room, part, p));
}

/** Whether a part stands in a room: the middle of what of it stands in the room
 *  inside the room's outline. */
export function standsIn(room: ProductInstance, part: ProductInstance): boolean {
  const foot = standingFoot(room, part);
  if (!room.polygon || room.polygon.length < 3 || !foot.length) return false;
  const c = foot.reduce((m, p) => ({ x: m.x + p.x / foot.length, y: m.y + p.y / foot.length }), { x: 0, y: 0 });
  return pointInPolygon(c, room.polygon);
}

/** A standalone sauna bench on a room's floor (room local). */
export interface OccupantBench {
  /** Its ribbon from its node line to `d` in front of it. */
  ribbon(d: number): Vec2[];
  /** Its skirting's face, its depth from the node line; null without one. */
  skirtFront: number | null;
  /** Its trestles' footprints. */
  trestles(): Vec2[][];
}

/** One thing standing on a room's floor, in the room's frame (ungrown). */
export interface FloorOccupant {
  part: ProductInstance;
  /** What of it stands in the room: set into a wall, its band out of the
   *  wall; else its footprint. A standalone bench's ribbon. */
  foot: Vec2[];
  /** Its whole outline on the floor: a guarded part's (a heater's) out to its
   *  cover, else to its air gap; a tilted part's, the box its body fills;
   *  anything else's whole footprint, into a wall too. A standalone bench's
   *  ribbon. */
  outline: Vec2[];
  /** Its height band over the room's floor (mm). */
  z0: number;
  z1: number;
  /** A standalone sauna bench's own; null for anything else. */
  bench: OccupantBench | null;
}

/** A part's whole outline in its own frame: a guarded part's out to its cover
 *  (else its air gap); a tilted one's, the box its tilted body fills; else its
 *  footprint. */
function outlineOf(p: ProductInstance): Vec2[] {
  if (traitsOf(p.category).guarded || !isTilted(p)) return partOuterCorners(p);
  const { hw, hd } = tiltedExtents(p, { w: p.width, d: p.depth, h: partHeight(p) });
  return [{ x: -hw, y: -hd }, { x: hw, y: -hd }, { x: hw, y: hd }, { x: -hw, y: hd }];
}

/** A standalone sauna bench: a slatted run along its nodes. */
function isNodeBench(p: ProductInstance): boolean {
  return p.category === 'saunabench' && !!p.nodes && p.nodes.length >= 2;
}

/** What of `parts` stands on a room's floor: a standalone sauna bench whose
 *  node line's middle is in the room, any other part that stands in it
 *  ({@link standsIn}); never the room itself or another room. Hidden and tilted
 *  parts included: each reader decides. */
export function floorOccupants(room: ProductInstance, parts: readonly ProductInstance[]): FloorOccupant[] {
  if (!room.polygon || room.polygon.length < 3) return [];
  const floor = roomBase(room);
  const local = (pts: readonly Vec2[]): Vec2[] => pts.map((q) => toInstanceLocal(room, q));
  const out: FloorOccupant[] = [];
  for (const p of parts) {
    if (p === room || p.autoBenches) continue;
    const [b0, b1] = partSpan(p);
    const band = { z0: b0 - floor, z1: b1 - floor };
    if (isNodeBench(p)) {
      const nodes = p.nodes!;
      const c = nodes.reduce((m, q) => ({ x: m.x + q.x / nodes.length, y: m.y + q.y / nodes.length }), { x: 0, y: 0 });
      if (!pointInPolygon(toInstanceLocal(room, c), room.polygon)) continue;
      const ribbon = (d: number): Vec2[] => local(ribbonOutline(nodes, d));
      const whole = ribbon(Math.max(1, p.depth));
      out.push({
        part: p, foot: whole, outline: whole, ...band,
        bench: { ribbon, skirtFront: nodeBenchSkirtFront(p), trestles: () => benchSupportMarks(p).map((m) => local(m.poly)) },
      });
      continue;
    }
    if (!standsIn(room, p)) continue;
    out.push({ part: p, foot: standingFoot(room, p, 0), outline: outlineOf(p).map((q) => inRoom(room, p, q)), ...band, bench: null });
  }
  return out;
}
